import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  api,
  ApiError,
  type ListedLocation,
  type ProducerSafePoi,
  type Requirement,
} from '../lib/api.js';
import '../styles/locations.css';
import { Callout } from './Callout.js';
import { Disclosure } from './ui/Disclosure.js';
import { EmptyState } from './ui/EmptyState.js';

/**
 * Finding a place and putting it against a requirement.
 *
 * THE HALF OF THE LOOP THAT WAS MISSING. A candidate could only appear by the
 * matching engine proposing one or by somebody using the "Add to project" modal
 * on the public site — so inside the workspace the Locations tab was two fixed
 * lists with no way to go and look for anything.
 *
 * TWO POPULATIONS, SHOWN AS TWO, because they are not interchangeable. A listed
 * location has an owner who offers it for filming; a point of interest is a
 * place a scout noticed, whose owner has agreed to nothing and may never. The
 * second is deliberately vague — a derived label and a coarsened position — and
 * the card says why, so nobody plans a shoot around one thinking it is booked.
 */
export function InventorySearch({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [country, setCountry] = useState('');
  const [typeId, setTypeId] = useState('');
  const [includePois, setIncludePois] = useState(true);
  const [target, setTarget] = useState('');
  const [note, setNote] = useState<string | null>(null);

  // A keystroke is not a query. Without this every letter typed is a round trip
  // and a LIKE scan over the whole inventory.
  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(term);
    }, 300);
    return () => {
      clearTimeout(id);
    };
  }, [term]);

  const facets = useQuery({
    queryKey: ['inventory-facets', projectId],
    queryFn: () => api.inventoryFacets(projectId),
  });

  const results = useQuery({
    queryKey: ['inventory', projectId, debounced, country, typeId, includePois],
    queryFn: () =>
      api.searchInventory(projectId, {
        ...(debounced ? { q: debounced } : {}),
        ...(country ? { country } : {}),
        ...(typeId ? { typeId: Number(typeId) } : {}),
        includePois,
      }),
  });

  // Only requirements that are actually being sourced can take a candidate —
  // offering the rest would produce a refusal the producer cannot act on.
  const requirements = useQuery({
    queryKey: ['matchable', projectId],
    queryFn: () => api.matchableRequirements(projectId),
  });

  const attach = useMutation({
    mutationFn: ({ type, id }: { type: 'LOCATION' | 'POI'; id: number }) =>
      api.attachCandidate(projectId, target, type, id),
    onSuccess: async (result) => {
      setNote(
        result.created
          ? 'Added to the shortlist.'
          : result.restored
            ? 'That one had been rejected — it is back on the shortlist.'
            : 'That one is already on the shortlist.',
      );
      await queryClient.invalidateQueries({ queryKey: ['compare', projectId] });
    },
    onError: (e: unknown) => {
      setNote(e instanceof ApiError ? e.message : 'Could not add that one.');
    },
  });

  const canAttach = target !== '';
  const data = results.data;
  const empty =
    data !== undefined && data.locations.length === 0 && data.pois.length === 0;

  return (
    <section data-testid="inventory-search" style={{ display: 'grid', gap: 14 }}>
      <div className="loc-section-head" style={{ marginBottom: 0 }}>
        <h3>Search the catalogue</h3>
        <p className="loc-section-note">
          ScoutAround&rsquo;s listed locations and the places scouts have found — add any of them to a
          location you are searching for.
        </p>
      </div>

      <div className="ui-panel" style={{ padding: 14, display: 'grid', gap: 10 }}>
        <div className="loc-filters">
          <label className="loc-field">
            <span className="flbl">Search</span>
            <input
              value={term}
              placeholder="warehouse, lighthouse, Sofia…"
              data-testid="inventory-q"
              onChange={(e) => {
                setTerm(e.target.value);
              }}
            />
          </label>

          <label className="loc-field">
            <span className="flbl">Country</span>
            <select
              value={country}
              data-testid="inventory-country"
              onChange={(e) => {
                setCountry(e.target.value);
              }}
            >
              <option value="">Any</option>
              {facets.data?.countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>

          <label className="loc-field">
            <span className="flbl">Type</span>
            <select
              value={typeId}
              data-testid="inventory-type"
              onChange={(e) => {
                setTypeId(e.target.value);
              }}
            >
              <option value="">Any</option>
              {facets.data?.types.map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label className="loc-field" style={{ flex: '1 1 280px' }}>
            <span className="flbl">Add to requirement</span>
            <select
              value={target}
              data-testid="inventory-target"
              onChange={(e) => {
                setTarget(e.target.value);
                setNote(null);
              }}
            >
              <option value="">Choose a requirement being searched…</option>
              {requirements.data?.map((r: Requirement) => (
                <option key={r.id} value={r.id}>
                  {r.title}
                </option>
              ))}
            </select>
          </label>

          <label className="loc-check" style={{ height: 38 }}>
            <input
              type="checkbox"
              checked={includePois}
              data-testid="inventory-include-pois"
              onChange={(e) => {
                setIncludePois(e.target.checked);
              }}
            />
            Include scout finds
          </label>
        </div>
      </div>

      {requirements.data && requirements.data.length === 0 ? (
        <Callout tone="ok" title="You are not searching for anything yet" testId="inventory-no-targets">
          You can browse, but a location has to be added to something the production needs. Approve a
          location requirement in the breakdown and press Start searching, then come back.
        </Callout>
      ) : null}

      {note !== null ? (
        <p className="mm" style={{ fontSize: 12.5, margin: 0 }} data-testid="inventory-note" role="status">
          {note}
        </p>
      ) : null}

      {results.isPending ? (
        <p className="mm" style={{ fontSize: 13, margin: 0 }} data-testid="inventory-loading">
          Searching…
        </p>
      ) : null}

      {empty ? (
        <EmptyState testId="inventory-empty" title="Nothing matched.">
          Try a broader term, or clear the country and type filters.
        </EmptyState>
      ) : null}

      {data?.truncated ? (
        <p className="mm" style={{ fontSize: 12, margin: 0 }} data-testid="inventory-truncated">
          Showing the first results only — narrow the search to see the rest.
        </p>
      ) : null}

      {data && data.locations.length > 0 ? (
        <Disclosure
          summary={`Listed locations (${String(data.locations.length)})`}
          defaultOpen
          testId="inventory-listed-group"
        >
          <PagedList
            items={data.locations}
            testId="inventory-listed-more"
            render={(l) => (
              <ListedRow
                key={l.sourceId}
                location={l}
                canAttach={canAttach}
                busy={attach.isPending}
                onAttach={() => {
                  attach.mutate({ type: 'LOCATION', id: l.sourceId });
                }}
              />
            )}
          />
        </Disclosure>
      ) : null}

      {data && data.pois.length > 0 ? (
        <Disclosure
          summary={`Scout finds (${String(data.pois.length)})`}
          defaultOpen={data.locations.length === 0}
          testId="inventory-poi-group"
        >
          <p className="mm" style={{ fontSize: 12, margin: '0 0 8px', lineHeight: 1.6, maxWidth: '68ch' }}>
            {/* Said once, above the group, rather than on every card. Nobody has
                approached these owners — shortlisting one starts that
                conversation rather than reserving anything. */}
            Places a scout noticed. Nobody has agreed terms for them, so the name and exact position
            are withheld until one is verified — shortlist one and we will look into it.
          </p>
          <PagedList
            items={data.pois}
            testId="inventory-poi-more"
            render={(p) => (
              <PoiRow
                key={p.sourceId}
                poi={p}
                canAttach={canAttach}
                busy={attach.isPending}
                onAttach={() => {
                  attach.mutate({ type: 'POI', id: p.sourceId });
                }}
              />
            )}
          />
        </Disclosure>
      ) : null}
    </section>
  );
}

/** Twenty rows, then "Show more" — a catalogue answer can be hundreds long. */
const CATALOGUE_PAGE = 20;

function PagedList<T>({ items, render, testId }: { items: readonly T[]; render: (item: T) => ReactNode; testId: string }) {
  const [limit, setLimit] = useState(CATALOGUE_PAGE);
  const shown = items.slice(0, limit);

  return (
    <>
      <ul className="loc-list">{shown.map(render)}</ul>
      {items.length > shown.length ? (
        <div className="loc-more">
          <button
            type="button"
            className="ctl"
            data-testid={testId}
            onClick={() => {
              setLimit(limit + CATALOGUE_PAGE);
            }}
          >
            {`Show ${String(Math.min(CATALOGUE_PAGE, items.length - shown.length))} more of ${String(items.length - shown.length)}`}
          </button>
        </div>
      ) : null}
    </>
  );
}

function ListedRow({
  location,
  canAttach,
  busy,
  onAttach,
}: {
  location: ListedLocation;
  canAttach: boolean;
  busy: boolean;
  onAttach: () => void;
}) {
  const where = [location.city, location.countryCode].filter(Boolean).join(', ');

  return (
    <li data-testid={`inventory-location-${String(location.sourceId)}`}>
      <span className="loc-list-title">{location.title}</span>
      {location.typeName !== null ? <span className="chip">{location.typeName}</span> : null}
      {where !== '' ? (
        <span className="mm" style={{ fontSize: 11.5 }}>
          {where}
        </span>
      ) : null}
      <AddButton
        canAttach={canAttach}
        busy={busy}
        onAttach={onAttach}
        testId={`inventory-add-location-${String(location.sourceId)}`}
      />
    </li>
  );
}

function PoiRow({
  poi,
  canAttach,
  busy,
  onAttach,
}: {
  poi: ProducerSafePoi;
  canAttach: boolean;
  busy: boolean;
  onAttach: () => void;
}) {
  return (
    <li data-testid={`inventory-poi-${String(poi.sourceId)}`}>
      {/* A DERIVED label. The projection is the privacy boundary and the UI must
          not undo it by showing something the server was careful not to send. */}
      <span className="loc-list-title">{poi.displayLabel}</span>
      <span className="chip">{poi.readiness.toLowerCase()}</span>
      {poi.discoveredMonth !== null ? (
        <span className="mm" style={{ fontSize: 11.5 }}>
          found {poi.discoveredMonth}
        </span>
      ) : null}
      <AddButton
        canAttach={canAttach}
        busy={busy}
        onAttach={onAttach}
        testId={`inventory-add-poi-${String(poi.sourceId)}`}
      />
    </li>
  );
}

function AddButton({
  canAttach,
  busy,
  onAttach,
  testId,
}: {
  canAttach: boolean;
  busy: boolean;
  onAttach: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      className="ctl"
      style={{ marginLeft: 'auto' }}
      disabled={!canAttach || busy}
      // Says WHY it is disabled. "Add" that does nothing with no explanation is
      // the most common way a working feature reads as broken.
      title={canAttach ? 'Add to the chosen requirement' : 'Choose a requirement first'}
      data-testid={testId}
      onClick={onAttach}
    >
      Add
    </button>
  );
}
